import React from 'react'
import { useSelector } from 'react-redux'
import { updateBetSlipFobRacingItemPrice } from '@mobi/betslip/Store/Bets'
import { selectBetSlipFobRacingSelectionIds } from '@mobi/betslip/Store/Bets/selectors'
import { selectWorkflowStatus } from '@mobi/betslip/Store/Workflow/selectors'
import { iotSubscribeTopics, iotUnsubscribeTopics } from '@mobi/utils/awsiot'
import {
  type FobPriceChangedPushEvent,
  PushEvent,
  SportsPriceChangedPushEvent,
  event$,
  getFobPriceChangeByPropositionTopic,
  isFobPriceChangedPushEvent,
  isSportsPriceChangedPushEvent,
} from '@core/State/PushData'
import { store } from '@core/Store'

export const useRealtimeFobPriceChanges = () => {
  const racingSubscriptionItems = useSelector(selectBetSlipFobRacingSelectionIds)
  const workflowStatus = useSelector(selectWorkflowStatus)

  const currentRaceSubscriptionsRef = React.useRef(new Set<string>())
  const workflowStatusRef = React.useRef(workflowStatus)

  workflowStatusRef.current = workflowStatus

  // Handle fob racing subscriptions
  React.useEffect(() => {
    racingSubscriptionItems.forEach(id => {
      if (currentRaceSubscriptionsRef.current.has(id)) return
      const topic = getFobPriceChangeByPropositionTopic(id)
      iotSubscribeTopics([topic])
      currentRaceSubscriptionsRef.current.add(id)
    })

    Array.from(currentRaceSubscriptionsRef.current)
      .filter(currId => !racingSubscriptionItems.some(id => id === currId))
      .forEach(currId => {
        iotUnsubscribeTopics([getFobPriceChangeByPropositionTopic(currId)])
        currentRaceSubscriptionsRef.current.delete(currId)
      })
  }, [racingSubscriptionItems])

  // Listen for price changes, update selections
  React.useEffect(() => {
    const fobPriceChangedSubscription = event$
      .filter(e => isPriceChangeEvent(e.payload))
      .subscribe(e => {
        if (
          workflowStatusRef.current === 'ready' &&
          isFobPriceChangedPushEvent(e.payload) &&
          currentRaceSubscriptionsRef.current.has(`${e.payload.propositionId}`)
        ) {
          store.dispatch(updateBetSlipFobRacingItemPrice(e.payload))
        }
      })

    return () => {
      fobPriceChangedSubscription.dispose()
    }
  }, [])
}

// =============
// Local Helpers
// =============

function isPriceChangeEvent(event: PushEvent): event is PriceChangeEvent {
  return isFobPriceChangedPushEvent(event) || isSportsPriceChangedPushEvent(event)
}

// =====
// Types
// =====

type PriceChangeEvent = FobPriceChangedPushEvent | SportsPriceChangedPushEvent
